import { createApiClient, type ApiClient } from '@khaddi/api-client';
import type { AuthResult, NavLayout, SessionUser, UiPrefs } from '@khaddi/contracts';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { withPrefs } from './prefs';

type SessionValue = {
  api: ApiClient;
  user: SessionUser | null;
  /** True until the stored token has been checked against /auth/me. */
  restoring: boolean;
  signIn: (result: AuthResult) => void;
  signOut: () => void;
  refreshUser: () => Promise<void>;
  /** Saves personal UI choices; applied at once and rolled back (rethrowing) if the server refuses. */
  updatePrefs: (prefs: UiPrefs) => Promise<void>;
};

const SessionContext = createContext<SessionValue | null>(null);

const read = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const write = (key: string, value: string | null): void => {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    // private mode: the session simply won't survive a reload
  }
};

export type SessionProviderProps = {
  /** localStorage key; differs per app so customer and shipper sessions don't collide. */
  storageKey: string;
  baseUrl?: string;
  children: ReactNode;
};

export const SessionProvider = ({ storageKey, baseUrl = '', children }: SessionProviderProps) => {
  const tokenRef = useRef<string | null>(read(storageKey));
  const [user, setUser] = useState<SessionUser | null>(null);
  const [restoring, setRestoring] = useState<boolean>(tokenRef.current !== null);

  const signOut = useCallback(() => {
    tokenRef.current = null;
    write(storageKey, null);
    setUser(null);
  }, [storageKey]);

  const api = useMemo(() => createApiClient({ baseUrl, getToken: () => tokenRef.current, onUnauthorized: signOut }), [baseUrl, signOut]);

  const refreshUser = useCallback(async () => {
    if (!tokenRef.current) return;
    const me = await api.auth.me();
    setUser(me.user);
  }, [api]);

  useEffect(() => {
    if (!tokenRef.current) return;
    refreshUser()
      .catch(() => signOut())
      .finally(() => setRestoring(false));
  }, [refreshUser, signOut]);

  const signIn = useCallback(
    (result: AuthResult) => {
      tokenRef.current = result.token;
      write(storageKey, result.token);
      setUser(result.user);
    },
    [storageKey],
  );

  // only the latest save may write back, so quick toggles with out-of-order replies settle on the last choice
  const prefsRequest = useRef(0);
  const updatePrefs = useCallback(
    async (prefs: UiPrefs) => {
      if (!user) return;
      const { id, prefs: previous } = user;
      const request = ++prefsRequest.current;
      setUser((current) => withPrefs(current, id, prefs));
      try {
        const saved = (await api.auth.updatePrefs(prefs)).user;
        if (request === prefsRequest.current) setUser((current) => withPrefs(current, id, saved.prefs));
      } catch (error) {
        if (request === prefsRequest.current) setUser((current) => withPrefs(current, id, previous));
        throw error;
      }
    },
    [api, user],
  );

  const value = useMemo(
    () => ({ api, user, restoring, signIn, signOut, refreshUser, updatePrefs }),
    [api, user, restoring, signIn, signOut, refreshUser, updatePrefs],
  );
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
};

export const useSession = (): SessionValue => {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession must be used inside <SessionProvider>');
  return value;
};

export const useApi = (): ApiClient => useSession().api;

/** The signed-in user's nav layout, or the app's default when they haven't picked one (or nobody is signed in). */
export const useNavLayout = (fallback: NavLayout): NavLayout => useSession().user?.prefs.navLayout ?? fallback;
